import { useState, useEffect } from 'react';
import { clearProductCache, getCacheMetadata } from '../api/hubspot';

interface CacheControlProps {
  onRefresh?: () => void;
}

/**
 * Cache Control Component
 *
 * Displays cache status and provides manual refresh functionality.
 * Shows:
 * - Last fetched time
 * - Expiry countdown
 * - Refresh button to manually clear cache and refetch
 */
export function CacheControl({ onRefresh }: CacheControlProps) {
  const [cacheInfo, setCacheInfo] = useState(getCacheMetadata());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  // Update cache info every second for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      const info = getCacheMetadata();
      setCacheInfo(info);

      if (info) {
        const remaining = info.expiresAt - Date.now();
        if (remaining > 0) {
          const minutes = Math.floor(remaining / 60000);
          const seconds = Math.floor((remaining % 60000) / 1000);
          setTimeRemaining(`${minutes}m ${seconds}s`);
        } else {
          setTimeRemaining('Expired');
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    clearProductCache();

    // Call parent's refresh handler if provided
    if (onRefresh) {
      await onRefresh();
    }

    // Update cache info after refresh
    setTimeout(() => {
      setCacheInfo(getCacheMetadata());
      setIsRefreshing(false);
    }, 500);
  };

  const formatLastFetched = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return `${mins} minute${mins > 1 ? 's' : ''} ago`;
    }
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    return date.toLocaleString();
  };

  if (!cacheInfo) {
    return (
      <div style={styles.container}>
        <div style={styles.status}>
          <span style={styles.dot('#999')}></span>
          <span style={styles.text}>No cache</span>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.status}>
        <span style={styles.dot('#4caf50')}></span>
        <div style={styles.info}>
          <div style={styles.label}>Cache active</div>
          <div style={styles.detail}>
            Last: {formatLastFetched(cacheInfo.lastFetched)}
          </div>
          <div style={styles.detail}>
            Expires: {timeRemaining}
          </div>
        </div>
      </div>

      <button
        onClick={handleRefresh}
        disabled={isRefreshing}
        style={{
          ...styles.button,
          ...(isRefreshing ? styles.buttonDisabled : {}),
        }}
        title="Clear cache and fetch fresh data from HubSpot"
      >
        {isRefreshing ? (
          <span style={styles.spinner}>⟳</span>
        ) : (
          '↻'
        )}
        {' '}
        Refresh
      </button>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '4px 0',
    fontSize: '13px',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dot: (color: string) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: color,
    flexShrink: 0,
  }),
  info: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  label: {
    fontWeight: 600,
    color: '#333',
  },
  detail: {
    fontSize: '11px',
    color: '#666',
  },
  text: {
    color: '#666',
  },
  button: {
    padding: '6px 12px',
    backgroundColor: '#ff7a59',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'background-color 0.2s',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  spinner: {
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
  },
} as const;

// Add global style for spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
